import React, { useMemo } from 'react';
import PatientProfileDataCompareTableHead from './PatientProfileDataCompareTableHead';
import PatientProfileDataCompareTableBody from './PatientProfileDataCompareTableBody';
import styles from './PatientProfileDataCompareTable.module.scss';
import Loading from '../../Loading';
import { PatientCompareDataTableColumn } from '../../../Modules/Patient/Interfaces/Patient/PatientCompareDataTableColumn.interface';
import apiClient from '../../../Services/ApiClient';
import { useAppContext } from '../../../Services/AppContext';
import useNotification from '../../../Hooks/useNotification.hook';
import pathToObject from '../../../Helpers/pathToObject.helper';
import PatientProfileDataCompareTableActions from './PatientProfileDataCompareTableActions';
import usePatientDataCompare from '../../../Hooks/Patient/usePatientDataCompare';
import useScope from '../../../Modules/Scope/Hooks/useScope';
import NCZIPatient from '../../../Modules/Nczi/Components/NCZIPatient/NCZIPatient';
import { Theme } from '../../../Enums/Theme.enum';
import Button from '../../Button/Button';
import NCZICommandResult from '../../../Modules/Nczi/Components/NCZICommandResult/NCZICommandResult';

interface Props {
	patientId: string;
}

const PatientProfileDataCompareTable = ({ patientId }: Props) => {
	const { workplaceId } = useAppContext();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const { isCzechia, isSlovakia } = useScope();

	const {
		virtualData,
		originalData,
		validationData,
		NCZIData,
		NCZICommandId,
		isLoading,
		isValidationDataLoading,
		refetch,
		workplaceMemberId,
		handleFetchNCZIData,
		handleFetchValidationData,
	} = usePatientDataCompare(patientId);

	const overwriteData = async (key: string, value: string) => {
		try {
			const response = await apiClient.patch(
				`/workplaces/${workplaceId}/patient/${workplaceMemberId}`,
				{ patient: pathToObject(key, value) },
			);

			if (response.status === 200) {
				showNotificationSuccess({
					content: 'base.shared.userEdited',
				});
				refetch();
			}
		} catch (error) {
			showNotificationAlert({
				content: 'base.shared.warningBug',
			});
		}
	};

	const columns: PatientCompareDataTableColumn[] = useMemo(() => {
		if (!(virtualData && originalData)) return [];

		const baseColumns: PatientCompareDataTableColumn[] = [
			{
				title: 'patientTreatment.profile.compareData.patientCard',
				data: virtualData,
			},
			{
				title: 'patientTreatment.profile.compareData.patientProfileInMeddiApp',
				data: originalData,
				compareTo: virtualData,
				overwriteData,
			},
		];

		if (validationData) {
			baseColumns.push({
				title: 'patientTreatment.profile.compareData.dataFromCssz',
				data: validationData,
				compareTo: virtualData,
				overwriteData,
			});
		}

		if (NCZIData) {
			baseColumns.push({
				title: 'patientTreatment.profile.compareData.dataFromNczi',
				data: NCZIData,
				compareTo: virtualData,
				overwriteData,
			});
		}

		return baseColumns;
	}, [virtualData, originalData, validationData, NCZIData, overwriteData]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<table className={styles.table}>
				<PatientProfileDataCompareTableHead columns={columns} />
				<PatientProfileDataCompareTableBody columns={columns} />
			</table>
			<PatientProfileDataCompareTableActions>
				{isCzechia && !validationData && (
					<Button
						title='patientTreatment.profile.compareData.csszButtonTitle'
						theme={Theme.BLUE}
						isLoading={isValidationDataLoading}
						onClick={() => handleFetchValidationData()}
					/>
				)}
				{isSlovakia && !NCZIData && (
					<NCZIPatient>
						<>
							{NCZICommandId ? (
								<NCZICommandResult
									commandId={NCZICommandId}
									handleRetryCommand={handleFetchNCZIData}
								/>
							) : (
								<Button
									title='patientTreatment.profile.compareData.ncziButtonTitle'
									theme={Theme.BLUE}
									onClick={() => handleFetchNCZIData()}
								/>
							)}
						</>
					</NCZIPatient>
				)}
			</PatientProfileDataCompareTableActions>
		</div>
	);
};

export default PatientProfileDataCompareTable;
